// File: frontend/src/components/auth/CookieBanner.js

import React, { useState, useEffect } from 'react';
import { useLanguage } from '../../hooks/useLanguage';
import { STORAGE_KEYS } from '../../config/constants';
import apiService from '../../services/apiService';
import { getCurrentUserContext } from '../../utils/bootstrap';
import styles from './CookieBanner.module.css';

const CookieBanner = () => {
  const { t } = useLanguage();
  const [isVisible, setIsVisible] = useState(false);
  const [showCustomize, setShowCustomize] = useState(false);
  const [preferences, setPreferences] = useState({
    necessary: true,
    analytics: false,
    marketing: false
  });

  useEffect(() => {
    const consent = localStorage.getItem(STORAGE_KEYS.COOKIE_CONSENT);
    if (!consent) {
      setIsVisible(true);
    }
  }, []);

  useEffect(() => {
    if (showCustomize) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
  
    return () => {
      document.body.style.overflow = '';
    };
  }, [showCustomize]);

  const handleAcceptAll = async () => {
    const allPreferences = {
      necessary: true,
      analytics: true,
      marketing: true
    };
    await savePreferences(allPreferences);
  };

  const handleAcceptNecessary = async () => {
    const necessaryOnly = {
      necessary: true,
      analytics: false,
      marketing: false
    };
    await savePreferences(necessaryOnly);
  };

  const handleCustomize = () => {
    setShowCustomize(true);
  };

  const handleSaveCustom = async () => {
    await savePreferences(preferences);
    setShowCustomize(false);
  };

  const handleTogglePreference = (key) => {
    if (key === 'necessary') return;
    
    setPreferences(prev => ({
      ...prev,
      [key]: !prev[key]
    }));
  };

  const savePreferences = async (prefs) => {
    try {
      const userContext = getCurrentUserContext();
      const now = new Date().toISOString();
      
      // ✅ FIXED: Match backend expected format
      if (userContext?.userId) {
        const response = await apiService.executeCommand(
          'updateConsentPreferences',
          {
            user_id: userContext.userId,
            consent_type: 'cookies', // ✅ Required by backend
            preferences: {
              necessary: prefs.necessary,
              analytics: prefs.analytics,
              marketing: prefs.marketing,
              consented_at: now // ✅ Timestamp
            }
          },
          userContext.userId,
          false // Public endpoint
        );

        if (!response.success) {
          console.warn('Failed to save consent to backend:', response.error);
        } else {
        //   console.log('✅ Consent saved to backend:', response.data);
        }
      }

      // Always save to localStorage
      localStorage.setItem(STORAGE_KEYS.COOKIE_CONSENT, JSON.stringify({
        preferences: prefs,
        timestamp: now
      }));

      setIsVisible(false);
    } catch (error) {
      console.error('Failed to save cookie preferences:', error);
      
      // Still save locally even if API fails
      localStorage.setItem(STORAGE_KEYS.COOKIE_CONSENT, JSON.stringify({
        preferences: prefs,
        timestamp: new Date().toISOString()
      }));
      setIsVisible(false);
    }
  };

  if (!isVisible) return null;

  return (
    <>
      {/* Main Banner */}
      <div className={styles.cookieBanner}>
        <div className={styles.container}>
          <div className={styles.content}>
            <div className={styles.icon}>
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
                <circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="2"/>
                <circle cx="8" cy="10" r="1.5" fill="currentColor"/>
                <circle cx="16" cy="10" r="1.5" fill="currentColor"/>
                <circle cx="12" cy="15" r="1.5" fill="currentColor"/>
                <circle cx="8" cy="16" r="1" fill="currentColor"/>
                <circle cx="16" cy="16" r="1" fill="currentColor"/>
              </svg>
            </div>
            
            <div className={styles.text}>
              <h3 className={styles.title}>
                {t('cookie_banner.title')}
              </h3>
              <p className={styles.description}>
                {t('cookie_banner.description')}
              </p>
            </div>
          </div>

          <div className={styles.actions}>
            <button
              className={styles.acceptButton}
              onClick={handleAcceptAll}
            >
              {t('cookie_banner.accept_all')}
            </button>
            
            <button
              className={styles.necessaryButton}
              onClick={handleAcceptNecessary}
            >
              {t('cookie_banner.necessary_only')}
            </button>
            
            <button
              className={styles.customizeButton}
              onClick={handleCustomize}
            >
              {t('cookie_banner.customize')}
            </button>
          </div>
        </div>
      </div>

      {/* Customization Modal */}
      {showCustomize && (
        <div className={styles.modalBackdrop} onClick={() => setShowCustomize(false)}>
          <div className={styles.modal} onClick={(e) => e.stopPropagation()}>
            <div className={styles.modalHeader}>
              <h2 className={styles.modalTitle}>
                {t('cookie_banner.customize_title')}
              </h2>
              <button 
                className={styles.modalClose}
                onClick={() => setShowCustomize(false)}
                aria-label="Close"
              >
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <line x1="18" y1="6" x2="6" y2="18"></line>
                  <line x1="6" y1="6" x2="18" y2="18"></line>
                </svg>
              </button>
            </div>

            <div className={styles.modalBody}>
              <p className={styles.modalDescription}>
                {t('cookie_banner.customize_description')}
              </p>

              {/* Necessary Cookies */}
              <div className={styles.cookieCategory}>
                <div className={styles.categoryHeader}>
                  <div className={styles.categoryInfo}>
                    <h3 className={styles.categoryTitle}>
                      {t('cookie_banner.necessary_title')}
                    </h3>
                    <p className={styles.categoryDescription}>
                      {t('cookie_banner.necessary_description')}
                    </p>
                  </div>
                  <div className={styles.toggleWrapper}>
                    <div className={`${styles.toggle} ${styles.toggleDisabled}`}>
                      <div className={styles.toggleSlider}></div>
                    </div>
                    <span className={styles.toggleLabel}>
                      {t('cookie_banner.always_active')}
                    </span>
                  </div>
                </div>
              </div>

              {/* Analytics Cookies */}
              <div className={styles.cookieCategory}>
                <div className={styles.categoryHeader}>
                  <div className={styles.categoryInfo}>
                    <h3 className={styles.categoryTitle}>
                      {t('cookie_banner.analytics_title')}
                    </h3>
                    <p className={styles.categoryDescription}>
                      {t('cookie_banner.analytics_description')}
                    </p>
                  </div>
                  <div className={styles.toggleWrapper}>
                    <button
                      className={`${styles.toggle} ${preferences.analytics ? styles.toggleActive : ''}`}
                      onClick={() => handleTogglePreference('analytics')}
                      aria-label="Toggle analytics cookies"
                    >
                      <div className={styles.toggleSlider}></div>
                    </button>
                  </div>
                </div>
              </div>

              {/* Marketing Cookies */}
              <div className={styles.cookieCategory}>
                <div className={styles.categoryHeader}>
                  <div className={styles.categoryInfo}>
                    <h3 className={styles.categoryTitle}>
                      {t('cookie_banner.marketing_title')}
                    </h3>
                    <p className={styles.categoryDescription}>
                      {t('cookie_banner.marketing_description')}
                    </p>
                  </div>
                  <div className={styles.toggleWrapper}>
                    <button
                      className={`${styles.toggle} ${preferences.marketing ? styles.toggleActive : ''}`}
                      onClick={() => handleTogglePreference('marketing')}
                      aria-label="Toggle marketing cookies"
                    >
                      <div className={styles.toggleSlider}></div>
                    </button>
                  </div>
                </div>
              </div>
            </div>

            <div className={styles.modalFooter}>
              <button
                className={styles.modalCancelButton}
                onClick={() => setShowCustomize(false)}
              >
                {t('cookie_banner.cancel')}
              </button>
              <button
                className={styles.modalSaveButton}
                onClick={handleSaveCustom}
              >
                {t('cookie_banner.save_preferences')}
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default CookieBanner;